import React from "react";

import { IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { ChevronDownIcon, DotsVerticalIcon, Typography } from "components";
import { Permissions } from "configs";
import { usePermission } from "hooks";
import { rem } from "utils";
import { getPrimaryColor } from "utils/theme";

import CommonButton from "./CommonButton";

type MenuConfig = {
  icon?: React.ReactNode;
  label: string;
  disabled?: boolean;
  permission?: keyof typeof Permissions;
  onClick: () => void;
  hide?: boolean;
  tooltip?: string;
};

interface MenuButtonProps {
  menus: MenuConfig[];
  type?: "icon" | "button" | "icon-mobile";
  labelButton?: string;
}

const MenuButton: React.FC<MenuButtonProps> = ({
  menus,
  type = "icon",
  labelButton,
}) => {
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (callback?: MenuConfig["onClick"]) => {
    if (callback) {
      callback();
    }
    setAnchorEl(null);
  };
  const { canAccess } = usePermission();

  return (
    <>
      {type === "icon-mobile" && (
        <CommonButton name="menu-icon" onClick={handleClick} />
      )}
      {type === "icon" && (
        <IconButton sx={{ padding: 0 }} onClick={handleClick}>
          <DotsVerticalIcon fontSize="small" />
        </IconButton>
      )}
      {type === "button" && (
        <CommonButton
          endIcon={<ChevronDownIcon fontSize="small" />}
          variant="outlined"
          color="inherit"
          label={labelButton}
          onClick={handleClick}
        />
      )}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => handleClose()}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& li:hover": {
            backgroundColor: getPrimaryColor()[100],
          },
        }}
      >
        {menus
          .filter((item) => !item.hide)
          .map((menu) => {
            const menuItem = (
              <MenuItem
                key={menu.label}
                onClick={() => handleClose(menu.onClick)}
                disableRipple
                disabled={
                  menu.disabled ||
                  (menu?.permission
                    ? !canAccess(menu.permission as never)
                    : false)
                }
                sx={{ columnGap: rem(10) }}
              >
                {menu?.icon}
                <Typography fontWeight={400} type="TextSM" color="grey.700">
                  {menu.label}
                </Typography>
              </MenuItem>
            );

            return menu.tooltip ? (
              <Tooltip key={menu.label} title={menu.tooltip} placement="left">
                <span>{menuItem}</span>
              </Tooltip>
            ) : (
              menuItem
            );
          })}
      </Menu>
    </>
  );
};

export default MenuButton;
